import { Controller } from 'stimulus';

/**
 * Disables a button for a configurable cooldown period, showing a countdown.
 * Persists the expiry timestamp in localStorage so the cooldown survives
 * page navigations and offcanvas close/reopen.
 *
 * When pendingText is set the countdown label reads "{pendingText} 4:59"
 * instead of the default "{originalText} (4:59)".
 *
 * @example
 * <div data-controller="cooldown-button"
 *      data-cooldown-button-seconds-value="300"
 *      data-cooldown-button-key-value="webhook_42">
 *   <button data-cooldown-button-target="button"
 *           data-action="click->cooldown-button#start">
 *     <span data-cooldown-button-target="label">Retry</span>
 *   </button>
 * </div>
 */
export default class extends Controller {
  static targets = ['button', 'label'];
  static values = {
    seconds: { type: Number, default: 300 },
    key: String,
    pendingText: { type: String, default: '' },
  };

  connect() {
    this.originalText = this.labelTarget.textContent;

    // Listen on document per Turbo integration standard — Turbo re-enables
    // the submitter after turbo:submit-end so we must re-assert disabled state.
    this._onSubmitEnd = (event) => {
      if (!this.element.contains(event.target)) return;
      if (this.remaining > 0) this.disable();
    };
    document.addEventListener('turbo:submit-end', this._onSubmitEnd);

    const expiry = this.getExpiry();
    if (expiry) {
      const remaining = Math.ceil((expiry - Date.now()) / 1000);
      if (remaining > 0) {
        this.remaining = remaining;
        this.disable();
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
        return;
      }
      this.clearExpiry();
    }

    if (this.element.dataset.cooldownButtonAutostart !== undefined) {
      this.start();
    }
  }

  start() {
    this.remaining = this.secondsValue;
    this.setExpiry(Date.now() + this.secondsValue * 1000);
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  tick() {
    if (!this.hasButtonTarget || !this.hasLabelTarget) {
      clearInterval(this.timer);
      return;
    }

    if (this.remaining <= 0) {
      clearInterval(this.timer);
      this.clearExpiry();
      this.buttonTarget.disabled = false;
      this.labelTarget.textContent = this.originalText;
      return;
    }

    const mins = Math.floor(this.remaining / 60);
    const secs = this.remaining % 60;
    const display =
      mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
    this.labelTarget.textContent = this.pendingTextValue
      ? `${this.pendingTextValue} ${display}`
      : `${this.originalText} (${display})`;
    this.remaining--;
  }

  disable() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = true;
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener('turbo:submit-end', this._onSubmitEnd);
  }

  get storageKey() {
    return `cooldown_${this.keyValue}`;
  }

  getExpiry() {
    const value = localStorage.getItem(this.storageKey);
    return value ? parseInt(value, 10) : null;
  }

  setExpiry(timestamp) {
    localStorage.setItem(this.storageKey, timestamp.toString());
  }

  clearExpiry() {
    localStorage.removeItem(this.storageKey);
  }
}
